<template>
  <nav aria-label="Tree" class="ui-tree">
    <TreeSearch
      v-model="searchStorage"
      v-model:search="clearedSearch"
      :number-found
      :is-loading
      :placeholder
      :size
      :hide-arrows="isSearchInfoVisible"
      :container-ref
    />
    <UIAlert v-if="isSearchInfoVisible" type="warn" class="ui-tree__info">
      Search degraded due to large data size.
      <slot name="info" />
    </UIAlert>
    <div ref="containerRef" class="ui-tree__container">
      <TreeItem
        :collapsed
        :node
        :key-value="root"
        :path="[root]"
        :hidable-names
        :active-path
        is-root
        is-opened-container
        @click:name="handleClickName"
        @click:node="handleClickNode"
        @on:contextmenu="handleOnContextmenu"
        @container:scroll="handleContainerScroll"
        @container:getscroll="handleGetContainerScroll"
      >
        <template #default="slotProps">
          <slot name="meta" v-bind="slotProps" />
        </template>
        <template #before="slotProps">
          <slot name="before-item" v-bind="slotProps" />
        </template>
        <template #name>
          <slot name="root" />
        </template>
        <template #description="slotProps">
          <slot name="description" v-bind="slotProps" />
        </template>
        <template v-if="$slots.actions" #actions="slotProps">
          <slot name="actions" v-bind="slotProps" />
        </template>
      </TreeItem>
      <slot name="after" />
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, provide, ref, watch, useTemplateRef } from 'vue'
import { get, has, pick, set } from 'lodash'
import { debouncedRef, watchPausable } from '@vueuse/core'

import {
  Tree,
  TreeNodeDescriptionGetter,
  TreeNodeFormatter,
} from './utils/types'
import { InputSize } from '@types'

import {
  DEBOUNCE_DELAY,
  DEGRADED_SEARCH_THRESHOLD,
  HASH_DELIMITER,
  TREE_ITEM_FOUND_CLASS,
  TREE_LAZY_CLASS,
} from './utils/const'

import { waitForNode } from '@/helpers/common'
import { generateHashData, scrollToNode } from './utils/helpers'

import TreeItem from './components/TreeItem.vue'
import TreeSearch from './components/TreeSearch.vue'
import UIAlert from '../Alert/Alert.vue'

type Props = {
  data?: Tree
  root: string

  metaFields?: string[]
  wide?: boolean
  collapsed?: boolean
  searchInMeta?: boolean
  hidableNames?: boolean

  placeholder?: string
  size?: InputSize

  formatter?: TreeNodeFormatter
  getDescription?: TreeNodeDescriptionGetter

  sortField?: string
  sortDir?: 'asc' | 'desc'

  activePath?: string[]
}

type Emits = {
  'click:name': [item: Tree, isLeaf: boolean]
  'click:node': [path: string[], value: any]
  'on:contextmenu': [item: Tree, isLeaf: boolean]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const searchStorage = defineModel<string>('search', { default: '' })

defineOptions({
  name: 'UITree',
})

const containerRef = useTemplateRef('containerRef')

const clearedSearch = ref('')
const debouncedSearch = debouncedRef(clearedSearch, DEBOUNCE_DELAY)
const isSearchInfoVisible = ref(false)

provide('metaFields', props.metaFields)
provide('search', debouncedSearch)
provide('wide', props.wide)
provide('formatter', props.formatter)
provide('getDescription', props.getDescription)
provide('sortField', props.sortField)
provide('sortDir', props.sortDir || 'desc')

const isLoading = ref(false)

const loaderTimeout = ref<NodeJS.Timeout>()

const highlightedNodeIndex = ref(0)

provide('highlightedNodeIndex', highlightedNodeIndex)

const hashData = computed(() =>
  generateHashData(
    props.data,
    props.metaFields,
    props.formatter,
    props.getDescription,
    true,
  ),
)
const foundData = computed(() =>
  debouncedSearch.value
    ? Object.entries(hashData.value).filter(([key, { value }]) => {
      if (key === props.root) return
      const search = debouncedSearch.value.toLowerCase()
      const keys = key.split(HASH_DELIMITER)
      return (
        keys.at(-1)?.includes(search) ||
            (props.searchInMeta && `${value}`.toLowerCase().includes(search))
      )
    })
    : [],
)
const searchResult = computed(() => foundData.value.map(([key]) => key))
const numberFound = computed(() => searchResult.value.length)
provide('searchResult', searchResult)

const node = computed(() => {
  if (!foundData.value.length) return get(props.data, [props.root])
  const result = {}
  foundData.value.forEach(([, { path }]) => {
    const computedPath: string[] = []
    path.forEach(item => {
      computedPath.push(item)
      if (has(result, computedPath)) return
      const node = get(props.data, computedPath)
      if (!node) return
      set(result, computedPath, pick(node, props.metaFields || []))
    })
  })
  return get(result, [props.root])
})

const handleOnContextmenu = (item: Tree, isLeaf: boolean) => {
  emit('on:contextmenu', item, isLeaf)
}

const handleClickName = (item: Tree, isLeaf: boolean) => {
  emit('click:name', item, isLeaf)
}

const handleClickNode = (path: string[], value: any) => {
  emit('click:node', path, value)
}

const handleGetContainerScroll = (callback: (top: number) => void) => {
  callback(containerRef.value?.scrollTop || 0)
}

const handleContainerScroll = (top: number) => {
  containerRef.value?.scrollTo({ top })
}

watch(searchStorage, () => {
  loaderTimeout.value && clearTimeout(loaderTimeout.value)
  isLoading.value = true
  pause()
  nextTick(resume)
})

watch(debouncedSearch, () => {
  isSearchInfoVisible.value = numberFound.value > DEGRADED_SEARCH_THRESHOLD && !!containerRef.value?.querySelector(`.${TREE_LAZY_CLASS}`)
  loaderTimeout.value = setTimeout(() => {
    isLoading.value = false
  }, DEBOUNCE_DELAY)
  if (!highlightedNodeIndex.value) return
  highlightedNodeIndex.value = 0
})

const { pause, resume } = watchPausable(numberFound, async value => {
  loaderTimeout.value && clearTimeout(loaderTimeout.value)
  isLoading.value = false
  if (!value) return
  await waitForNode(`.${TREE_ITEM_FOUND_CLASS}`)
  highlightedNodeIndex.value = 1
  scrollToNode(containerRef.value, highlightedNodeIndex.value)
})
</script>

<style>
.ui-tree {
  @apply flex flex-col flex-auto;
  @apply max-w-full;

  &__info {
    @apply p-2 mb-2;
    @apply rounded;

    .ui-alert__message {
      @apply text-sm sm:text-xs;
      @apply font-normal;
    }
  }

  &__container {
    @apply flex-auto;
    @apply md:h-0;
    @apply overflow-y-auto;
    @apply overflow-x-hidden;
  }
}
</style>
